import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AppUseSelector, AppUseDispatch } from '../../redux/hooks'
import {
  saveLastStep,
  setStep,
  getStory,
  wordDoKnow,
  wordDoesNotKnow,
  getWordDetail,
} from '../../redux/actions/storyAction'
import { getQuestion, getAnswer } from '../../redux/actions/questionAction'
import { getPracticeWords, getWriteSpeakState } from '../../redux/actions/writeSpeakAction'
import { AddWordToHackChinese, handleAddWordToSkritter } from '../../redux/actions/userAction'
import { getCurrentStreak } from '../../redux/actions/streakAction'
import NavigationFooter from '../../containers/navigation-footer/NavigationFooter'
import WithRedirect from 'containers/WithRedirect'
import WordDetail from 'containers/word-detail'
import WordExamplesModal from 'containers/modal/word-examples-modal'
import Loader from 'containers/loader/loader'
import WriteSpeakStep from 'components/write-speak/WriteSpeakStep'
import LeaveConfirm from 'components/write-speak/LeaveConfirm'
import { QUIZ, STORY_FINISHED, HOME } from 'routes/routeConstant'
import { membershipNameType, MemberStepInStory, storyStep } from '../../utils/constant'
import { REGEX } from 'utils/regex'
import { getWordLine, getWordTypeFromStory, wordSimplified, getSentenceWords } from 'utils/helperFunction'
import { CHECK_WORD_IS_CLICKED, SET_SUBMIT_QUESTION, SET_WORD_DETAIL } from '../../redux/types'
import { PracticeWord, WriteSpeakState } from 'types/writeSpeakTypes'
import { useLessonDiscussionsEnabled } from 'components/discussion/useLessonDiscussionsEnabled'
import { Discussion, DiscussionAnswer, fetchDiscussion, markNotificationsRead, wordAdded } from 'components/discussion/discussionApi'
import DiscussionPreview from 'components/discussion/DiscussionPreview'
import DiscussionPanel from 'components/discussion/DiscussionPanel'
import { useDiscussionThreads } from 'components/discussion/DiscussionList'
import { announceNotificationsChanged } from 'components/discussion/DiscussionBits'

const PUNCTUATION_ONLY = /^[\s&.\-”“"'，、；：:;,.!！？?@#$%^*()（）《》「」。…]+$/

/**
 * Story step 4, "Writing & Speaking" (route /question/:id). This container
 * owns the route plumbing (story, question, tracking), the one-tap dictionary
 * and the Back/Finish bar; the step itself lives in components/write-speak.
 */
const Question: React.FC = (props: any) => {
  const {
    lastStepEnum,
    isGetData,
    storyLevel,
    story,
    credentials,
    membership,
    subscriptionStatus,
    currentStreak,
    user_auth_token,
    skritter_token,
  } = props
  const { memberId, metaData, customFields } = credentials
  const { question, curAnswer, answers, questionContent, count } = props.question
  const { id: storyId = '' } = useParams()
  const { t } = useTranslation('quiz')
  const navigate = useNavigate()
  const dispatch = AppUseDispatch()
  const characterPreference = credentials?.customFields?.character
  const { wordDetails, isWordClick, storyDictionary } = AppUseSelector(({ story }) => story)
  const extraDivRef = useRef<HTMLDivElement>(null)
  const contentWordRef = useRef<HTMLDivElement>(null)
  const [gettingQuestion, setGettingQuestion] = useState(false)
  const [questionWord, setQuestionWord] = useState<any>(questionContent ? [...questionContent] : [])
  const [answerLoading, setAnswerLoading] = useState(false)
  const [activeIndex, setActiveIndex] = useState('-1')
  const [activeInfoWord, setActiveInfoWord] = useState<string | null>(null)
  const [wordDetailVisible, setWordDetailVisible] = useState(false)
  const [openExampleModal, setOpenExampleModal] = useState(false)
  const [isImportanceOpen, setImportanceOpen] = useState(false)
  const [isMasteryOpen, setMasteryOpen] = useState(false)
  const [wsState, setWsState] = useState<WriteSpeakState | null>(null)
  const [wsStateLoaded, setWsStateLoaded] = useState(false)
  const [practiceWords, setPracticeWords] = useState<PracticeWord[]>([])
  const [leaveTarget, setLeaveTarget] = useState<'finish' | 'back' | null>(null)
  // A popup opened from "Words to use" is a check, not a look-up: closing it
  // must not count against the word, or struggling words would stay struggling.
  const suppressLookupRef = useRef(false)
  const unsentRef = useRef(false)

  // ---------- lesson discussions (PostHog flag lesson-discussions) ----------
  const discussionsOn = useLessonDiscussionsEnabled()
  const [discussion, setDiscussion] = useState<Discussion | null>(null)
  const [discussionLoading, setDiscussionLoading] = useState(false)
  const [panelOpen, setPanelOpen] = useState(false)
  const [panelFocus, setPanelFocus] = useState<string | null>(null)
  const lookedUpIn = useRef(new Set<string>())
  const loadDiscussion = useCallback(() => {
    if (!storyId) return
    setDiscussionLoading(true)
    fetchDiscussion(storyId)
      .then(setDiscussion)
      .catch(() => undefined)
      .finally(() => setDiscussionLoading(false))
  }, [storyId])
  const threads = useDiscussionThreads(
    discussion,
    (update) => setDiscussion((d) => (d ? update(d) : d)),
    loadDiscussion,
    panelFocus,
  )
  const openPanel = (answerRef?: string | null) => {
    setPanelFocus(answerRef || null)
    setPanelOpen(true)
    // Opening the discussion is seeing the replies to your answer.
    const unread = discussion?.unread || []
    if (unread.length) {
      markNotificationsRead(unread.map((u) => u.id)).then(announceNotificationsChanged)
      setDiscussion((d) => (d ? { ...d, unread: [] } : d))
    }
  }

  const planName = membership?.name
  const isStandard = planName == membershipNameType.Standard
  const isStandardTrial = isStandard && subscriptionStatus === 'trialing'
  const memberName = customFields?.['first-name'] || metaData?.firstName || ''

  useEffect(() => {
    window.scrollTo(0, 0)
    setStep(storyStep.Step3)
  }, [])

  useEffect(() => {
    if (JSON.stringify(story) === '{}') {
      getStory(storyId, storyLevel)
    }
  }, [isGetData, storyId, storyLevel])

  useEffect(() => {
    if (!currentStreak) {
      getCurrentStreak()
    }
  }, [currentStreak])

  useEffect(() => {
    if (!storyId || !storyLevel) return
    if (lastStepEnum !== null && lastStepEnum < storyStep.Step3) {
      saveLastStep(MemberStepInStory.QUESTION, storyLevel, storyId)
    }
    if (!props?.question?.question) {
      setGettingQuestion(true)
      getQuestion(storyId, storyLevel)
        .then((result: any) => setQuestionWord([...(result?.questionContent || [])]))
        .catch((error) => console.error('Error getting question:', error))
        .finally(() => setGettingQuestion(false))
    }
  }, [storyId, storyLevel])

  // The plan state and the word list load in parallel with the question; neither blocks first paint.
  useEffect(() => {
    if (!storyId || !storyLevel) return
    let cancelled = false
    setWsStateLoaded(false)
    getWriteSpeakState(storyId, storyLevel).then((state) => {
      if (cancelled) return
      setWsState(state)
      setWsStateLoaded(true)
    })
    getPracticeWords(storyId, storyLevel, memberId).then((words) => {
      if (!cancelled) setPracticeWords(words)
    })
    return () => {
      cancelled = true
    }
  }, [storyId, storyLevel, memberId])

  const loadAnswers = useCallback(
    (perPage: number) => {
      setAnswerLoading(true)
      getAnswer(storyId, storyLevel, memberId, null as any, null as any, perPage)
        .catch(() => undefined)
        .finally(() => setAnswerLoading(false))
    },
    [storyId, storyLevel, memberId],
  )

  const onAnswerSaved = useCallback(
    (answer: string) => {
      dispatch({ type: SET_SUBMIT_QUESTION, payload: answer })
      loadAnswers(Math.max(8, (answers?.length || 0) + 1))
      if (discussionsOn) loadDiscussion()
    },
    [answers?.length, loadAnswers, discussionsOn, loadDiscussion],
  )

  // Warn before closing the tab with a recording that was never sent (the in-page
  // confirm covers Back and Finish; a closing tab only allows the browser's own prompt).
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!unsentRef.current) return
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [])

  // Every word we know on this page, for tapping into unsegmented text (the learner's
  // own answer, another story's title).
  const known = useMemo(() => {
    const map = new Map<string, { pinyin?: string; english?: string }>()
    const add = (w: any) => {
      const word = (w?.word || '').replace(/<[^>]+>/g, '')
      if (word && !map.has(word)) map.set(word, { pinyin: w?.pinyin, english: w?.english })
    }
    const dictionary: Record<string, any> = storyDictionary || {}
    Object.keys(dictionary).forEach((k) => add(dictionary[k]))
    ;(questionContent || []).forEach(add)
    ;(story?.storyContent || []).forEach(add)
    practiceWords.forEach(add)
    ;(answers || []).forEach((a: any) => (a?.answerContent || []).forEach(add))
    ;(discussion?.answers || []).forEach((a) => {
      a.content.forEach(add)
      a.replies.forEach((r) => r.content.forEach(add))
    })
    return map
  }, [storyDictionary, questionContent, story?.storyContent, practiceWords, answers, discussion])

  // ---------- one-tap dictionary ----------

  const closePopup = () => {
    setWordDetailVisible(false)
    setImportanceOpen(false)
    setMasteryOpen(false)
    setActiveIndex('-1')
    setActiveInfoWord(null)
  }

  const countLookupIfAny = () => {
    if (suppressLookupRef.current) return
    if (wordDetailVisible && wordDetails?.word && !isWordClick) {
      const mastery = getWordTypeFromStory(wordDetails?.word, story, characterPreference)
      wordDoesNotKnow({
        word: wordSimplified(wordDetails?.word),
        storyId,
        mastery: mastery === 'word' ? undefined : mastery,
        page: 'story',
      })
      dispatch({ type: CHECK_WORD_IS_CLICKED, payload: false })
    }
  }

  useEffect(() => {
    if (openExampleModal) return
    const handleClickOutside = (event: any) => {
      const target = event.target as HTMLElement
      if (contentWordRef.current?.contains(target) || extraDivRef.current?.contains(target)) return
      countLookupIfAny()
      if (!target.closest?.('[data-specific-buttons]') && !target.closest?.('[data-ws-info]')) closePopup()
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [openExampleModal, wordDetailVisible, isWordClick, wordDetails, story])

  const onWordClickIntegration = (obj: any) => {
    if (user_auth_token) {
      AddWordToHackChinese({ word: obj?.word, pinyin: obj?.pinyin, user_auth_token })
    }
    if (skritter_token) {
      handleAddWordToSkritter({
        word: obj?.word,
        translate: { pinyin: obj?.pinyin, english: obj?.english },
        title: story?.storyTitleContent,
        skritter_token,
      })
    }
  }

  const onClickWord = (event: any, wordObj: any, sentence: any, key: string) => {
    event?.preventDefault?.()
    event?.stopPropagation?.()
    const raw = (wordObj?.word || '').replace(/<[^>]+>/g, '')
    if (!raw || wordObj?.plain || REGEX.specialChars.test(raw) || PUNCTUATION_ONLY.test(raw)) return
    const word = wordSimplified(raw)
    suppressLookupRef.current = false
    setActiveInfoWord(null)
    setActiveIndex(activeIndex === key ? '-1' : key)
    // Looked-up words drop out of the "known" list sent on Finish, as on the story page.
    setQuestionWord((prev: any[]) => prev.filter((w: any) => w?.word !== wordObj?.word))
    if (!memberId) return
    setWordDetailVisible(true)
    const mastery = getWordTypeFromStory(word, story, characterPreference)
    getWordDetail({
      detail: {
        word,
        pinyin: wordObj?.pinyin,
        english: wordObj?.english,
        sentenceString: getWordLine(sentence),
        sentenceWords: getSentenceWords(sentence),
      },
      payload: { word, memberId },
      wordType: mastery,
    } as any).then((res: any) =>
      onWordClickIntegration({ word: res?.word, pinyin: res?.pinyin, english: res?.english }),
    )
  }

  // A word tapped in someone else's answer (or a reply under it) is a look-up like any other. The
  // answer's author hears about it once a day: "3 learners added words from your answer".
  const onDiscussionWord = (answer: DiscussionAnswer) => (event: any, wordObj: any, sentence: any, key: string) => {
    onClickWord(event, wordObj, sentence, key)
    if (!answer.mine && !lookedUpIn.current.has(answer.ref)) {
      lookedUpIn.current.add(answer.ref)
      wordAdded(answer.ref)
    }
  }

  // Info button on a "Words to use" card: the usual popup, but read-only. It is filled from
  // the practice-words payload, then enriched from the story's cached dictionary or
  // words/get-clicked-word, neither of which records a look-up. Closing it skips
  // words/update-clicked-word (the call that counts look-ups).
  const onInfoWord = (w: PracticeWord) => {
    if (activeInfoWord === w.word && wordDetailVisible) {
      closePopup()
      return
    }
    suppressLookupRef.current = true
    setActiveIndex('-1')
    setActiveInfoWord(w.word)
    setWordDetailVisible(true)
    const detail = {
      word: w.word,
      pinyin: w.pinyin,
      english: w.english,
      mastery: w.mastery,
      importance: w.importance,
      struggling: w.struggling,
      lookups: w.lookups,
    }
    dispatch({ type: SET_WORD_DETAIL, payload: { loader: false, detail } })
    if (memberId) {
      getWordDetail({ detail, payload: { word: w.word, memberId }, wordType: w.mastery } as any)
    }
  }

  // ---------- leaving ----------

  const finish = () => {
    const uniqueItems = {}
    questionWord.forEach((item) => {
      uniqueItems[item.word] = item
    })
    const wordList = Object.values(uniqueItems)
      ?.filter((wordInfo: any) => !REGEX.specialChars.test(wordInfo?.word))
      ?.map((wordInfo: any) => wordSimplified(wordInfo?.word))
    wordDoKnow({ wordList, storyId })
    navigate(STORY_FINISHED)
  }

  const back = () => navigate(`${QUIZ}/${storyId}`)

  const onNext = () => {
    if (unsentRef.current) setLeaveTarget('finish')
    else finish()
  }

  const onBack = () => {
    if (unsentRef.current) setLeaveTarget('back')
    else back()
  }

  const otherAnswers = useMemo(
    () =>
      (answers || []).map((a: any) => ({
        id: a?.id,
        name: a?.name,
        answer: a?.answer,
        answerContent: a?.answerContent,
      })),
    [answers],
  )

  return (
    <>
      <div className='section-2'>
        {gettingQuestion && !question ? (
          <Loader />
        ) : question && questionContent?.length > 0 ? (
          <WriteSpeakStep
            key={storyId}
            storyId={storyId}
            level={storyLevel}
            memberId={memberId}
            memberName={memberName}
            planName={planName}
            isStandard={isStandard}
            isStandardTrial={isStandardTrial}
            story={story}
            questionContent={questionContent}
            curAnswer={curAnswer || ''}
            wsState={wsState}
            wsStateLoaded={wsStateLoaded}
            words={practiceWords}
            answers={otherAnswers}
            answersTotal={typeof count === 'number' ? count : otherAnswers.length}
            answersLoading={answerLoading}
            onLoadAllAnswers={() => loadAnswers(Math.min(100, Math.max(count || 0, otherAnswers.length + 8)))}
            onAnswerSaved={onAnswerSaved}
            activeIndex={activeIndex}
            onClickWord={onClickWord}
            onInfoWord={onInfoWord}
            activeInfoWord={wordDetailVisible ? activeInfoWord : null}
            known={known}
            characterPreference={characterPreference}
            onUnsentChange={(unsent) => (unsentRef.current = unsent)}
            renderOthers={
              discussionsOn
                ? () => (
                    <DiscussionPreview
                      discussion={discussion}
                      loading={discussionLoading}
                      onShow={loadDiscussion}
                      onOpen={openPanel}
                      story={story}
                      activeIndex={activeIndex}
                      onWordTap={onDiscussionWord}
                      onToggleLike={threads.toggleLike}
                    />
                  )
                : undefined
            }
          />
        ) : (
          <div className='section-story w-container'>
            <div className='w-row'>
              <div className='w-col w-col-12' style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div className='item-title' style={{ marginBottom: '20px', fontSize: '32px', fontWeight: 'bold' }}>
                  {t('question.notFoundTitle')}
                </div>
                <div style={{ marginBottom: '30px', fontSize: '18px' }}>
                  <p>{t('question.notFoundBody')}</p>
                </div>
                <button className='button-2 w-button' onClick={() => navigate(HOME)} style={{ marginTop: '20px' }}>
                  {t('question.goHome')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <NavigationFooter
        className={'right-active'}
        backPath={`${QUIZ}/${storyId}`}
        backOnclick={onBack}
        nextText={t('question.finish')}
        nextPath={STORY_FINISHED}
        nextOnclick={onNext}
      />
      <LeaveConfirm
        open={leaveTarget !== null}
        onStay={() => setLeaveTarget(null)}
        onLeave={() => {
          const target = leaveTarget
          setLeaveTarget(null)
          unsentRef.current = false
          if (target === 'back') back()
          else finish()
        }}
      />
      {discussionsOn && (
        <DiscussionPanel
          open={panelOpen}
          onClose={() => setPanelOpen(false)}
          threads={threads}
          loading={discussionLoading}
          story={story}
          activeIndex={activeIndex}
          onWordTap={onDiscussionWord}
          focusRef={panelFocus}
        />
      )}
      <WordDetail
        ref={contentWordRef}
        setOpenExampleModal={setOpenExampleModal}
        isImportanceOpen={isImportanceOpen}
        setImportanceOpen={setImportanceOpen}
        isMasteryOpen={isMasteryOpen}
        setMasteryOpen={setMasteryOpen}
        setWordDetailVisible={setWordDetailVisible}
        setActiveIndex={setActiveIndex}
        visible={wordDetailVisible}
        openExampleModal={openExampleModal}
        showMasterySection={true}
      />
      <WordExamplesModal
        ref={extraDivRef}
        openExampleModal={openExampleModal}
        setOpenExampleModal={setOpenExampleModal}
        setWordDetailVisible={setWordDetailVisible}
      />
    </>
  )
}

const mapStateToProps = (state: any) => ({
  credentials: state.user.credentials,
  user_auth_token: state.user.user_auth_token,
  skritter_token: state.user.skritter_token,
  membership: state.user.membership,
  subscriptionStatus: state.user.subscriptionStatus,
  question: state.question,
  errors: state.UI.errors,
  story: state.story.currentStory,
  storyLevel: state.story.storyLevel,
  lastStepEnum: state.story.lastStepEnum,
  isGetData: !state.story.errors && (JSON.stringify(state.story.currentStory) === '{}' || state.story.currentStory.id),
  currentStreak: state.streak.current,
})

export default connect(mapStateToProps)(WithRedirect(Question))
